import { LanguageToggle, useT } from '@/lib/i18n';
import { useEffect, useState, type CSSProperties } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useLocation, useMatch, useNavigate } from 'react-router-dom';
import {
  Briefcase,
  Building2,
  ChevronDown,
  Contact2,
  FileSignature,
  Home,
  LifeBuoy,
  LogOut,
  Menu,
  MessageCircle,
  Moon,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  Receipt,
  Search,
  Settings,
  Sparkles,
  Sun,
  UserPlus,
  X,
} from 'lucide-react';
import { useSession, useLogout } from '@/features/auth/use-session';
import { useChatUnread } from '@/features/chat/api';
import { AssistantPanel } from '@/features/ai/assistant-panel';
import { useAppBadge } from '@/hooks/use-app-badge';
import { usePermission } from '@/hooks/use-permission';
import { Avatar } from '@/components/ui/misc';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { PushPrompt } from '@/components/shared/push-prompt';
import { BrandMark } from '@/components/shared/brand';
import { apiList } from '@/lib/api';
import { onServiceWorkerMessage } from '@/lib/pwa';
import { useTheme } from '@/lib/theme';
import { cn } from '@/lib/utils';
import { CommandPalette } from './command-palette';
import { NotificationBell } from './notification-bell';
import { ImpersonationBanner } from './impersonation-banner';
import { TrialBanner } from './trial-banner';
import { useHomeRoute, useQuickItems, useVisibleSections, type NavItem, type NavSection } from './nav';

const COLLAPSE_KEY = 'dovo.nav.collapsed';
const OPEN_KEY = 'dovo.nav.open';

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}
function writeJson(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Not remembered; fine.
  }
}

/**
 * The app frame: one slate chrome — a top bar with search, the assistant and
 * quick actions, and a grouped sidebar beside it — around a light work canvas
 * with a rounded corner, so the records are what the eye lands on.
 */
export function AppShell() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => readJson(COLLAPSE_KEY, false));
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const home = useHomeRoute();

  const inConversation = Boolean(useMatch('/chat/:channelId'));
  const pageEditor = Boolean(useMatch('/marketing/pages/:pageId'));
  const fullBleed = Boolean(useMatch('/inbox')) || pageEditor;

  const { data: notifications } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => apiList<{ unread: number }>('/notifications', { params: { limit: 10 } }),
    refetchInterval: 60_000,
  });
  useAppBadge(notifications?.data.unread);
  useEffect(() => onServiceWorkerMessage((message) => navigate(message.link)), [navigate]);

  const toggleCollapsed = () =>
    setCollapsed((value) => {
      writeJson(COLLAPSE_KEY, !value);
      return !value;
    });

  return (
    <div
      className="flex h-dvh w-full flex-col overflow-hidden bg-sidebar"
      style={inConversation ? ({ '--bottom-nav': '0px' } as CSSProperties) : undefined}
    >
      <ImpersonationBanner />
      <TrialBanner />

      <TopBar
        home={home}
        onMenu={() => setMobileOpen(true)}
        onSearch={() => setPaletteOpen(true)}
        onAssistant={() => setAssistantOpen((value) => !value)}
      />

      <div className="flex min-h-0 flex-1">
        {mobileOpen && (
          <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setMobileOpen(false)} aria-hidden />
        )}
        <Sidebar
          collapsed={collapsed}
          mobileOpen={mobileOpen}
          onNavigate={() => setMobileOpen(false)}
          onCloseMobile={() => setMobileOpen(false)}
          onToggleCollapsed={toggleCollapsed}
          onExpand={() => {
            if (collapsed) toggleCollapsed();
          }}
        />

        <main
          className={cn(
            'surface-app scrollbar-thin min-w-0 flex-1 lg:rounded-ss-xl',
            fullBleed
              ? 'overflow-hidden pb-[var(--bottom-nav)] md:pb-0'
              : 'overflow-y-auto p-4 pb-[calc(1rem+var(--bottom-nav))] lg:px-7 lg:py-6',
          )}
        >
          <div className={fullBleed ? 'h-full' : 'mx-auto max-w-[96rem] animate-fade-in'}>
            {!inConversation && !fullBleed && <PushPrompt />}
            <Outlet />
          </div>
        </main>
      </div>

      {!inConversation && <MobileTabBar home={home} onMenu={() => setMobileOpen(true)} />}

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <AssistantPanel open={assistantOpen} onClose={() => setAssistantOpen(false)} />
    </div>
  );
}

// ── Top bar ───────────────────────────────────────────────────────────────────

function TopBar({
  home,
  onMenu,
  onSearch,
  onAssistant,
}: {
  home: string;
  onMenu: () => void;
  onSearch: () => void;
  onAssistant: () => void;
}) {
  const t = useT();
  const { can } = usePermission();
  const navigate = useNavigate();
  const { data: user } = useSession();
  const logout = useLogout();
  const { theme, setTheme } = useTheme();

  const creates = [
    { label: 'Contact', icon: Contact2, to: '/crm/contacts', permission: 'crm.contact.create' },
    { label: 'Company', icon: Building2, to: '/crm/companies', permission: 'crm.company.create' },
    { label: 'Deal', icon: Briefcase, to: '/crm/deals', permission: 'crm.deal.create' },
    { label: 'Lead', icon: UserPlus, to: '/crm/leads', permission: 'crm.lead.create' },
    { label: 'Quote', icon: FileSignature, to: '/crm/quotes/new', permission: 'crm.quote.create' },
    { label: 'Invoice', icon: Receipt, to: '/finance/invoices/new', permission: 'finance.invoice.create' },
    { label: 'Ticket', icon: LifeBuoy, to: '/service/tickets', permission: 'service.ticket.create' },
  ].filter((item) => can(item.permission));

  const iconButton = 'flex h-9 w-9 items-center justify-center rounded-md text-white/75 transition-colors hover:bg-white/10 hover:text-white';

  return (
    <header className="relative z-30 flex h-[var(--app-header)] shrink-0 items-center gap-2 px-2 text-white sm:px-3">
      <button className={cn(iconButton, 'lg:hidden')} onClick={onMenu} aria-label="Open menu">
        <Menu className="h-5 w-5" />
      </button>
      <Link to={home} className="flex shrink-0 items-center gap-2 rounded-md px-1.5 py-1" aria-label="DovoCRM home">
        <BrandMark size={28} />
        <span className="hidden text-[17px] font-extrabold tracking-[-0.04em] sm:inline" dir="ltr">
          dovo<span className="font-semibold text-sidebar-accent">crm</span>
        </span>
      </Link>

      <div className="mx-auto flex min-w-0 max-w-xl flex-1 items-center gap-2 ps-2">
        <button
          onClick={onSearch}
          className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-full bg-white/[0.09] px-3.5 text-sm text-white/60 ring-1 ring-inset ring-white/10 transition-colors hover:bg-white/[0.14] hover:text-white/85"
        >
          <Search className="h-4 w-4 shrink-0" />
          <span className="truncate">{t('Search DovoCRM')}</span>
          <kbd className="ms-auto hidden rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold text-white/60 sm:inline">
            Ctrl K
          </kbd>
        </button>
        <button
          onClick={onAssistant}
          className="hidden h-9 shrink-0 items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-white/90 transition-colors hover:bg-white/10 md:flex"
        >
          <Sparkles className="h-4 w-4 text-sidebar-accent" />
          {t('Ask Dovo')}
        </button>
      </div>

      <div className="flex shrink-0 items-center gap-0.5">
        {creates.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={iconButton} aria-label={t('Create')} title={t('Create')}>
                <Plus className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-2 py-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                {t('Create')}
              </div>
              {creates.map((item) => (
                <DropdownMenuItem key={item.label} onSelect={() => navigate(item.to)}>
                  <item.icon className="h-4 w-4 text-muted-foreground" />
                  {t(item.label)}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <button className={cn(iconButton, 'md:hidden')} onClick={onAssistant} aria-label="Ask Dovo">
          <Sparkles className="h-[18px] w-[18px] text-sidebar-accent" />
        </button>
        {can('admin.organization.view') && (
          <Link to="/admin/organization" className={cn(iconButton, 'hidden sm:flex')} title={t('Settings')}>
            <Settings className="h-[18px] w-[18px]" />
          </Link>
        )}
        <NotificationBell buttonClassName={iconButton} />
        <LanguageToggle tone="dark" className="hidden px-2 sm:inline-flex" />

        <span className="mx-1 hidden h-6 w-px bg-white/15 sm:block" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="flex items-center gap-2 rounded-md py-1 ps-1 pe-1.5 text-sm transition-colors hover:bg-white/10">
              <Avatar name={user?.name ?? '?'} size={28} />
              <span className="hidden max-w-[11rem] truncate font-semibold text-white/90 xl:inline">
                {user?.organization?.name ?? user?.name}
              </span>
              <ChevronDown className="h-3.5 w-3.5 text-white/60" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <div className="flex items-center gap-3 px-2 py-2">
              <Avatar name={user?.name ?? '?'} size={36} />
              <div className="min-w-0">
                <div className="truncate text-sm font-bold">{user?.name}</div>
                <div className="truncate text-xs text-muted-foreground">{user?.email}</div>
              </div>
            </div>
            <div className="mx-2 mb-1 rounded-md bg-muted px-2.5 py-2 text-xs">
              <div className="truncate font-semibold">{user?.organization?.name}</div>
              <div className="text-muted-foreground">
                {user?.organization?.plan === 'TRIAL' ? t('Free trial') : user?.organization?.plan}
                {user?.roles?.length ? ` · ${user.roles.map((role) => role.name).join(', ')}` : ''}
              </div>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/account')}>
              <Settings className="h-4 w-4" /> {t('Profile & preferences')}
            </DropdownMenuItem>
            {can('admin.organization.view') && (
              <DropdownMenuItem onSelect={() => navigate('/admin/billing')}>
                <Receipt className="h-4 w-4" /> {t('Plan & billing')}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onSelect={() => setTheme(theme === 'dark' ? 'light' : 'dark')}>
              {theme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              {theme === 'dark' ? t('Light mode') : t('Dark mode')}
            </DropdownMenuItem>
            <div className="sm:hidden">
              <LanguageToggle className="w-full justify-start px-2" />
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem destructive onSelect={() => logout.mutate()}>
              <LogOut className="h-4 w-4" /> {t('Sign out')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────

function Sidebar({
  collapsed,
  mobileOpen,
  onNavigate,
  onCloseMobile,
  onToggleCollapsed,
  onExpand,
}: {
  collapsed: boolean;
  mobileOpen: boolean;
  onNavigate: () => void;
  onCloseMobile: () => void;
  onToggleCollapsed: () => void;
  onExpand: () => void;
}) {
  const t = useT();
  const sections = useVisibleSections();
  const { pathname } = useLocation();
  const [open, setOpen] = useState<Record<string, boolean>>(() => readJson(OPEN_KEY, {}));

  const isActive = (item: NavItem) => (item.end ? pathname === item.to : pathname === item.to || pathname.startsWith(`${item.to}/`));
  const sectionActive = (section: NavSection) => section.items.some(isActive);

  // The group holding the current screen is always open.
  useEffect(() => {
    const current = sections.find((section) => section.label && sectionActive(section));
    if (current && !open[current.label]) {
      setOpen((value) => {
        const next = { ...value, [current.label]: true };
        writeJson(OPEN_KEY, next);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const toggle = (label: string) =>
    setOpen((value) => {
      const next = { ...value, [label]: !value[label] };
      writeJson(OPEN_KEY, next);
      return next;
    });

  // On phones the drawer always shows the full menu.
  const rail = collapsed && !mobileOpen;

  return (
    <aside
      className={cn(
        'fixed inset-y-0 start-0 z-50 flex shrink-0 flex-col bg-sidebar text-sidebar-foreground transition-[transform,width] duration-200 lg:static lg:z-auto lg:translate-x-0',
        rail ? 'lg:w-[4.25rem]' : 'w-[16.5rem] lg:w-60',
        mobileOpen ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full lg:rtl:translate-x-0',
      )}
    >
      <div className="flex h-14 items-center justify-between px-4 lg:hidden">
        <span className="flex items-center gap-2 text-white">
          <BrandMark size={28} />
          <span className="text-[17px] font-extrabold tracking-[-0.04em]" dir="ltr">
            dovo<span className="font-semibold text-sidebar-accent">crm</span>
          </span>
        </span>
        <button className="text-white/70" onClick={onCloseMobile} aria-label="Close menu">
          <X className="h-5 w-5" />
        </button>
      </div>

      <nav className="scrollbar-thin flex-1 overflow-y-auto px-2 pb-3 pt-1 [scrollbar-color:rgba(255,255,255,0.14)_transparent]">
        {sections.map((section, index) => {
          if (rail) {
            // Collapsed: pinned items as icons, every group as one icon that
            // opens the menu at that group.
            if (!section.label) {
              return section.items.map((item) => (
                <RailLink key={item.to} item={item} active={isActive(item)} label={t(item.label)} onNavigate={onNavigate} />
              ));
            }
            return (
              <button
                key={section.label}
                type="button"
                title={t(section.label)}
                onClick={() => {
                  if (!open[section.label]) toggle(section.label);
                  onExpand();
                }}
                className={cn(
                  'mx-auto my-0.5 flex h-10 w-10 items-center justify-center rounded-md transition-colors',
                  sectionActive(section) ? 'bg-sidebar-active text-white' : 'text-white/55 hover:bg-white/[0.07] hover:text-white',
                )}
              >
                <section.icon className="h-[18px] w-[18px]" />
              </button>
            );
          }

          if (!section.label) {
            return (
              <div key="pinned" className="mb-2 border-b border-white/[0.07] pb-2">
                {section.items.map((item) => (
                  <SideLink key={item.to} item={item} active={isActive(item)} label={t(item.label)} onNavigate={onNavigate} />
                ))}
              </div>
            );
          }

          const expanded = Boolean(open[section.label]);
          return (
            <div key={section.label} className={cn(index > 0 && 'mt-0.5')}>
              <button
                type="button"
                onClick={() => toggle(section.label)}
                className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-[13px] font-semibold text-white/50 transition-colors hover:text-white/85"
                aria-expanded={expanded}
              >
                <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', !expanded && '-rotate-90 rtl:rotate-90')} />
                {t(section.label)}
              </button>
              {expanded && (
                <div className="pb-1.5">
                  {section.items.map((item) => (
                    <SideLink key={item.to} item={item} active={isActive(item)} label={t(item.label)} onNavigate={onNavigate} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      <div className="hidden border-t border-white/[0.07] p-2 lg:block">
        <button
          type="button"
          onClick={onToggleCollapsed}
          className={cn(
            'flex h-9 items-center gap-2 rounded-md px-2.5 text-[13px] text-white/50 transition-colors hover:bg-white/[0.07] hover:text-white',
            rail ? 'mx-auto w-10 justify-center px-0' : 'w-full',
          )}
          title={rail ? t('Expand menu') : t('Collapse menu')}
        >
          {rail ? <PanelLeftOpen className="h-[18px] w-[18px] rtl:-scale-x-100" /> : <PanelLeftClose className="h-[18px] w-[18px] rtl:-scale-x-100" />}
          {!rail && t('Collapse')}
        </button>
      </div>
    </aside>
  );
}

function SideLink({ item, active, label, onNavigate }: { item: NavItem; active: boolean; label: string; onNavigate: () => void }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={cn(
        'group relative mb-px flex items-center gap-3 rounded-md px-2.5 py-[7px] text-[14px] transition-colors',
        active ? 'bg-sidebar-active font-semibold text-white' : 'text-sidebar-foreground hover:bg-white/[0.06] hover:text-white',
      )}
    >
      {active && <span className="absolute inset-y-1.5 start-0 w-[3px] rounded-full bg-sidebar-accent" />}
      <item.icon className={cn('h-[17px] w-[17px] shrink-0', active ? 'text-white' : 'text-white/45 group-hover:text-white/80')} />
      <span className="truncate">{label}</span>
    </NavLink>
  );
}

function RailLink({ item, active, label, onNavigate }: { item: NavItem; active: boolean; label: string; onNavigate: () => void }) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      title={label}
      className={cn(
        'relative mx-auto my-0.5 flex h-10 w-10 items-center justify-center rounded-md transition-colors',
        active ? 'bg-sidebar-active text-white' : 'text-white/55 hover:bg-white/[0.07] hover:text-white',
      )}
    >
      {active && <span className="absolute inset-y-2 -start-2 w-[3px] rounded-full bg-sidebar-accent" />}
      <item.icon className="h-[18px] w-[18px]" />
    </NavLink>
  );
}

// ── Phone tab bar ─────────────────────────────────────────────────────────────

function MobileTabBar({ home, onMenu }: { home: string; onMenu: () => void }) {
  const t = useT();
  const { can } = usePermission();
  const quick = useQuickItems();
  const hasChat = can('chat.channel.view');
  const { data: unread } = useChatUnread(hasChat);
  const chatUnread = unread?.total ?? 0;

  const tabs: (NavItem & { badge?: number })[] = [
    { to: home, label: 'Home', icon: Home, end: true },
    ...quick.filter((item) => item.to !== home),
    ...(hasChat ? [{ to: '/chat', label: 'Chat', icon: MessageCircle, badge: chatUnread }] : []),
  ];

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-30 flex h-[var(--bottom-nav)] items-stretch border-t bg-card pb-[env(safe-area-inset-bottom)] md:hidden"
      aria-label="Primary"
    >
      {tabs.map((tab) => (
        <NavLink
          key={tab.to}
          to={tab.to}
          end={tab.end}
          className={({ isActive }) =>
            cn(
              'relative flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-semibold',
              isActive ? 'text-primary' : 'text-muted-foreground',
            )
          }
        >
          <span className="relative">
            <tab.icon className="h-5 w-5" />
            {tab.badge ? (
              <span className="absolute -end-2.5 -top-1.5 min-w-[1.1rem] rounded-full bg-primary px-1 text-center text-[10px] font-semibold leading-4 text-primary-foreground">
                {tab.badge > 99 ? '99+' : tab.badge}
              </span>
            ) : null}
          </span>
          <span className="truncate">{t(tab.label)}</span>
        </NavLink>
      ))}
      <button
        type="button"
        onClick={onMenu}
        className="flex flex-1 flex-col items-center justify-center gap-0.5 text-[10px] font-semibold text-muted-foreground"
      >
        <Menu className="h-5 w-5" />
        {t('Menu')}
      </button>
    </nav>
  );
}

/** Exported for the empty-state buttons that want the same styling. */
export { Button as ShellButton };
